import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import { NumberStepper } from '../components/NumberStepper';
import {
  getPricingQuote,
  getTicketTypes,
  listSchedules,
  type KioskTicketType,
  type ScheduleSummary,
} from '../lib/api';
import { formatPrice, formatTime, pickI18n } from '../lib/format';
import { dayKeyOf, dayLabel, groupByDay } from '../lib/schedule-group';
import { useKiosk } from '../lib/session';
import { ticketTypeLabel } from '../lib/ticket-label';

/** 班次可售：未天候取消且尚有座位。 */
function isPurchasable(s: ScheduleSummary): boolean {
  return !s.weather_cancelled && s.capacity - s.reserved_count > 0;
}

/** 班次選擇 + 人數（票種由商品定義動態渲染）— Flow 3.7。 */
export function ScheduleScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, draft, patchDraft } = useKiosk();
  const product = draft.product;

  const [scheduleId, setScheduleId] = useState<string | null>(draft.schedule?.id ?? null);
  const [quantities, setQuantities] = useState<Record<string, number>>(draft.quantities);

  const schedulesQuery = useQuery({
    queryKey: ['schedules', product?.id],
    queryFn: () => listSchedules(product?.id ?? ''),
    enabled: product !== null,
    staleTime: 30_000,
  });

  // 直接導向此頁但無商品 → 回首頁。
  if (product === null) return <Navigate to="/home" replace />;

  const items = schedulesQuery.data?.items ?? [];
  /*
   * 從行程推薦進來時只顯示那一天。顧客在推薦頁答的是「8/21」，到了選班次卻看到
   * 兩週的清單，那個回答就等於白問。直接從商品瀏覽進來（沒有 recommendDate）
   * 則維持完整清單。
   */
  const scopedItems =
    draft.recommendDate === null
      ? items
      : items.filter((s) => dayKeyOf(s.departure_at) === draft.recommendDate);
  const selected = items.find((s) => s.id === scheduleId) ?? null;

  // 可售票種 + 各票種單價（商品定義；成人 ∪ ticket_prices）。
  const ticketTypesQuery = useQuery({
    queryKey: ['ticket-types', product.id, selected?.id],
    queryFn: () =>
      getTicketTypes({ product_id: product.id, schedule_id: selected!.id, channel: 'kiosk' }),
    enabled: selected !== null,
    staleTime: 30_000,
  });
  const sellableTypes = ticketTypesQuery.data?.ticket_types ?? [];

  const qtyOf = (tt: string): number => quantities[tt] ?? 0;
  const partySize = sellableTypes.reduce((sum, tt) => sum + qtyOf(tt.ticket_type), 0);
  const adultQty = qtyOf('adult');
  /*
   * 可買人數上限 = 該班次剩餘座位。原本每個票種各自寫死 20——大人 20 + 兒童 20 +
   * 敬老 20 就能點出 60 個人，而剩餘只有 3，顧客要一路走到結帳才被後端擋下。
   */
  const seatCap = selected === null ? 0 : selected.capacity - selected.reserved_count;
  const canProceed =
    selected !== null && isPurchasable(selected) && partySize >= 1 && partySize <= seatCap;

  // 同 server PricingResolver 解析折後 unit_price/total；確保顯示金額與落庫一致。
  const quoteTickets = sellableTypes
    .map((tt) => ({
      ticket_type: tt.ticket_type as KioskTicketType,
      quantity: qtyOf(tt.ticket_type),
    }))
    .filter((x) => x.quantity > 0);
  const quoteQuery = useQuery({
    queryKey: ['pricing.quote', product.id, selected?.id, JSON.stringify(quoteTickets)],
    queryFn: () =>
      getPricingQuote({
        product_id: product.id,
        schedule_id: selected!.id,
        channel: 'kiosk',
        tickets: quoteTickets,
      }),
    enabled: selected !== null && adultQty >= 1 && quoteTickets.length > 0,
    staleTime: 30_000,
  });

  const subtotalNum = quoteQuery.data
    ? Number(quoteQuery.data.total)
    : Number(product.base_price) * partySize;
  const subtotalIsResolved = quoteQuery.data !== undefined;
  const promoApplied =
    subtotalIsResolved && (quoteQuery.data?.lines.some((l) => l.pricing_id !== null) ?? false);
  // 原價總和（劃線價 × 數量）：優先取 server quote 各行 original_price；無 quote 時用票種表原價。
  const originalTotalNum = quoteQuery.data
    ? quoteQuery.data.lines.reduce((s, l) => s + Number(l.original_price) * l.quantity, 0)
    : sellableTypes.reduce((s, tt) => s + Number(tt.original_price) * qtyOf(tt.ticket_type), 0);
  const promoSavedNum = Math.max(0, originalTotalNum - subtotalNum);

  function proceed(): void {
    if (selected === null) return;
    // 只保留可售票種之數量，去除 stale 鍵
    const cleaned: Record<string, number> = {};
    for (const tt of sellableTypes) {
      const q = qtyOf(tt.ticket_type);
      if (q > 0) cleaned[tt.ticket_type] = q;
    }
    patchDraft({ schedule: selected, quantities: cleaned, quote: quoteQuery.data ?? null });
    navigate('/delivery');
  }

  return (
    <KioskFrame bleed>
      <HeroBanner height={340} align="left">
        <Mascot size={210} pose="smile" />
      </HeroBanner>

      <h1 className="k-h1" style={{ textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
        {pickI18n(product.name_i18n, product.sku, locale)}
      </h1>
      <p className="k-subtitle k-subtitle--flanked">{t('schedule.lead')}</p>

      <h2 className="k-sectionhead k-column">{t('schedule.pickLabel')}</h2>
      {schedulesQuery.isPending && <p className="k-text">{t('common.loading')}</p>}
      {schedulesQuery.isError && <p className="k-error">{t('common.errNetwork')}</p>}
      {schedulesQuery.isSuccess && items.length === 0 && (
        <p className="k-text k-text--dim">{t('schedule.noSchedule')}</p>
      )}

      {groupByDay(scopedItems).map(([dayKey, daySchedules]) => (
        <div key={dayKey} className="k-column">
          {/*
           * 設計稿的標題寫死「今日時段」，因為它畫的是從行程推薦帶著日期進來的
           * 情形。從商品瀏覽進來時清單會跨好幾天，日期標頭不能省，否則兩個
           * 「14:00」會分不出是哪一天。
           */}
          <h3 className="k-dayhead">{dayLabel(dayKey, locale, t)}</h3>
          <div className="k-slots">
            {daySchedules.map((s) => {
              const remaining = s.capacity - s.reserved_count;
              const disabled = !isPurchasable(s);
              const statusText = s.weather_cancelled
                ? t('schedule.cancelled')
                : t('schedule.soldOut');
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`k-slot${scheduleId === s.id ? ' k-slot--active' : ''}`}
                  aria-pressed={scheduleId === s.id}
                  disabled={disabled}
                  onClick={() => setScheduleId(s.id)}
                >
                  <span className="k-slot__time">
                    {formatTime(s.departure_at)} - {formatTime(s.departure_end_at)}
                  </span>
                  <hr className="k-slot__rule" />
                  <span className="k-slot__seats">
                    {disabled ? (
                      statusText
                    ) : (
                      <>
                        {t('schedule.remaining')}
                        <span className="k-slot__num">{remaining}</span>
                        <span className="k-slot__cap">/{s.capacity}</span>
                      </>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <hr className="k-hairline" />

      <h2 className="k-sectionhead k-column">{t('schedule.partyTitle')}</h2>

      <div className="k-column">
        {selected === null && <p className="k-text k-text--dim">{t('schedule.pickFirst')}</p>}
        {selected !== null && ticketTypesQuery.isPending && (
          <p className="k-text">{t('common.loading')}</p>
        )}
        {sellableTypes.map((tt) => {
          const label = ticketTypeLabel(tt.ticket_type, tt.display_name_i18n, locale);
          const unit = Number(tt.unit_price);
          const orig = Number(tt.original_price);
          const discounted = orig > unit;
          return (
            <NumberStepper
              key={tt.ticket_type}
              label={`${label}（${formatPrice(unit, product.currency)}）`}
              labelNode={
                <>
                  {label}（
                  {discounted && (
                    <span
                      style={{
                        textDecoration: 'line-through',
                        color: 'var(--c-text-dim)',
                        marginRight: 6,
                      }}
                    >
                      {formatPrice(orig, product.currency)}
                    </span>
                  )}
                  <span style={discounted ? { color: '#C2410C', fontWeight: 700 } : undefined}>
                    {formatPrice(unit, product.currency)}
                  </span>
                  ）
                </>
              }
              value={qtyOf(tt.ticket_type)}
              min={tt.ticket_type === 'adult' ? 1 : 0}
              // 綁「本票種現有人數 + 還能再加幾人」，各票種各給一個固定值會加爆
              max={Math.max(0, qtyOf(tt.ticket_type) + (seatCap - partySize))}
              onChange={(v) => setQuantities((q) => ({ ...q, [tt.ticket_type]: v }))}
            />
          );
        })}
        {sellableTypes.length > 0 && (
          <>
            <hr className="k-hairline" style={{ margin: '18px 0' }} />
            {promoSavedNum > 0 && (
              <>
                <div className="k-row" style={{ marginBottom: 4 }}>
                  <span className="k-text k-text--dim">{t('schedule.originalTotal')}</span>
                  <span className="k-text k-text--dim" style={{ textDecoration: 'line-through' }}>
                    {formatPrice(originalTotalNum, product.currency)}
                  </span>
                </div>
                <div className="k-row" style={{ marginBottom: 8 }}>
                  <span className="k-text" style={{ color: '#C2410C', fontWeight: 600 }}>
                    {t('schedule.promoSaved')}
                  </span>
                  <span className="k-text" style={{ color: '#C2410C', fontWeight: 600 }}>
                    −{formatPrice(promoSavedNum, product.currency)}
                  </span>
                </div>
              </>
            )}
            <div className="k-row">
              <span className="k-text" style={{ fontWeight: 700 }}>
                {t('schedule.subtotal')}（{partySize}）
              </span>
              <span className="k-amount k-amount--primary">
                {formatPrice(subtotalNum, product.currency)}
              </span>
            </div>
            <hr className="k-hairline" style={{ margin: '18px 0 0' }} />
            {promoApplied && (
              <p
                className="k-text k-text--dim"
                style={{ marginTop: 6, textAlign: 'right', fontSize: 14 }}
              >
                ✓ 已套用優惠
              </p>
            )}
          </>
        )}
      </div>

      <div className="k-footbar k-footbar--center">
        <KioskButton variant="secondary" onClick={() => navigate(-1)}>
          ← {t('common.back')}
        </KioskButton>
        <KioskButton variant="primary" disabled={!canProceed} onClick={proceed}>
          {t('schedule.toDelivery')} →
        </KioskButton>
      </div>
    </KioskFrame>
  );
}
